import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import Plot from "react-plotly.js";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { Toast } from "primereact/toast";
import MetadataTab from "./MetadataTab";
import ScriptEmotionPartModal from "../../components/IMSDBScripts/ScriptEmotionPartModal";
import axios from "axios";

const emotionOrder = [
  "joy","trust","fear","surprise","sadness","disgust","anger","anticipation"
];

const plutchikDyads = [
  { a: "joy",          b: "anticipation", name: "Optimism"        },
  { a: "joy",          b: "trust",        name: "Love"            },
  { a: "trust",        b: "fear",         name: "Submission"      },
  { a: "fear",         b: "surprise",     name: "Awe"             },
  { a: "surprise",     b: "sadness",      name: "Disappointment"  },
  { a: "sadness",      b: "disgust",      name: "Remorse"         },
  { a: "disgust",      b: "anger",        name: "Contempt"        },
  { a: "anger",        b: "anticipation", name: "Aggressiveness"  },
];

const emotionColors = {
  joy: "#F7D358",
  trust: "#1ABC9C",
  fear: "#2ECC71",
  surprise: "#58D3F7",
  sadness: "#2980B9",
  disgust: "#8E44AD",
  anticipation: "#F39C12",
  anger: "#E74C3C",
};

const TABS = ["Overview", "Parts", "Metadata", "Insights"];

// ===== helpers =====
const sum = (arr) => arr.reduce((a, b) => a + (Number(b) || 0), 0);
const mean = (arr) => (arr.length ? sum(arr) / arr.length : 0);
const stdev = (arr) => {
  if (!arr.length) return 0;
  const m = mean(arr);
  return Math.sqrt(mean(arr.map((v) => (v - m) ** 2)));
};

const formatHeadingTitle = (a, b, compareMode) => {
  if (compareMode && a && b) return `${a} vs ${b}`;
  if (a) return a;
  return "";
};

// ===== lista de acervos =====
const ACERVOS = [
  { name: "IMSDb — Internet Movie Script Database", url: "https://imsdb.com", note: "Clássico acervo de roteiros em texto" },
  { name: "Além do Roteiro", url: "https://alemdoroteiro.com/2019/05/21/roteiros-de-cinema-e-series-para-fins-educacionais", note: "PDFs de roteiros populares" },
  { name: "Simply Scripts", url: "https://www.simplyscripts.com", note: "Links to movie scripts, screenplays, transcripts, and excerpts from classic movies to current flicks to future films." },
  { name: "Script Slug", url: "https://www.scriptslug.com", note: "Several scripts" },
];

// estilos do heading
const headingWrapStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 12,
  width: "100%",
  margin: "8px 0 4px",
};

const headingTitleStyle = {
  margin: 0,
  fontSize: 22,
  fontWeight: 700,
  lineHeight: 1.25,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100%",
};

const IMSDBScripts = () => {
  // data
  const [generalData, setGeneralData] = useState([]);
  const [partsMap, setPartsMap] = useState({}); // { [title]: parts[] }

  // ui
  const [titles, setTitles] = useState([]);
  const [selectedTitle, setSelectedTitle] = useState(null);
  const [selectedTitleSource, setSelectedTitleSource] = useState(null);

  const [compareMode, setCompareMode] = useState(false);
  const [compareTitle, setCompareTitle] = useState(null);

  const [activeTab, setActiveTab] = useState(TABS[0]);

  // modal
  const [selectedPartText, setSelectedPartText] = useState(null);
  const [selectedPartNumber, setSelectedPartNumber] = useState(null);
  const [selectedPartDopa, setSelectedPartDopa] = useState(null);
  const [selectedPartScriptTitle, setSelectedPartScriptTitle] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  // upload
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const loadingIntervalRef = useRef(null);

  // abas
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoTab, setInfoTab] = useState("Overview");

  // ACERVOS modal
  const [acervosOpen, setAcervosOpen] = useState(false);
  const [acervosQuery, setAcervosQuery] = useState("");

  const toast = useRef(null);
  const base_url = process.env.REACT_APP_URL_IMSDB_BASE_URL;

  const loadingMessages = [
    "Analyzing script...",
    "Extracting emotions...",
    "Identifying dopamine peaks...",
    "Dividing script into parts...",
    "Calculating emotional balance...",
    "Refining data...",
    "Mapping emotional trajectory...",
    "Validating results...",
    "Generating final analysis...",
    "Finalizing process...",
  ];

  const getGeneral = useCallback(async () => {
    const res = await axios.get(`${base_url}/get-all-imsdb-scripts`);
    setGeneralData(res.data);

    const collator = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });
    const list = [...new Set((res.data || []).map(it => it?.title).filter(Boolean))].sort(collator.compare);
    const opts = list.map(t => ({ label: t, value: t }));
    setTitles(opts);

    // opcional: se nothing selecionado e há itens, preseleciona o primeiro
    // if (!selectedTitle && opts.length) setSelectedTitle(opts[0].value);
  }, [base_url /*, selectedTitle*/]);

  const fetchPartsFor = useCallback(async (title) => {
    if (!title) return [];
    const res = await axios.get(`${base_url}/get-all-imsdb-scripts-emotion-parts`, { params: { title } });
    return res.data;
  }, [base_url]);

  const ensurePartsLoaded = useCallback(async (title) => {
    if (!title) return;
    if (partsMap[title]) return;
    const data = await fetchPartsFor(title);
    setPartsMap((prev) => ({ ...prev, [title]: data }));
  }, [fetchPartsFor, partsMap]);

  const InfoDialog = () => {
    const thresholds = {
      hook:      { bad: "< 60", ok: "60–75", good: "> 75" },
      endArousal:{ bad: "< 45", ok: "45–60", good: "> 60" },
      endPos:    { bad: "< 45", ok: "45–55", good: "> 55" },
      ebi:       { bad: "< 0",  ok: "0–30",  good: "> 30" },
      pacing:    { low: "< 6 (linear)", sweet: "≈ 8–15", high: "> 20 (errático)" }
    };

    const k = kpisA || {};
    const number = (v) => (typeof v === "number" ? v.toFixed(1) : "—");

    const WheelLegend = () => (
      <div style={{display:"grid",gap:6,gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))"}}>
        {plutchikDyads.map((d)=>(
          <div key={d.name} style={{border:"1px solid #eee",borderRadius:8,padding:8}}>
            <strong>{d.name}</strong><br/>
            {d.a.toUpperCase()} + {d.b.toUpperCase()}
          </div>
        ))}
      </div>
    );

    const Para = ({children}) => <p style={{margin:"8px 0"}}>{children}</p>;
    const Small = ({children}) => <div style={{fontSize:12,opacity:0.8,marginTop:4}}>{children}</div>;

    let body = null;

    if (infoTab === "Overview") {
      body = (
        <>
          <h3 style={{marginTop:0}}>Como ler a aba “Overview”</h3>
          <Para><b>Plutchik Wheel</b>: gráfico polar com as 8 emoções na ordem da roda. Cores = intensidade (%). Emoções adjacentes formam combinações (“dyads”).</Para>
          <WheelLegend />
          <Small>Ex.: JOY + ANTICIPATION {`→`} <i>Optimism</i>. JOY + TRUST {`→`} <i>Love</i>.</Small>

          <hr/>

          <Para><b>Distribution (Pie)</b>: participação relativa de cada emoção no filme.</Para>
          <Para><b>Radar (Comparação)</b>: sobrepõe as 8 emoções entre dois filmes e mostra um “Fingerprint Δ”.</Para>

          <hr/>

          <h4>Métricas</h4>
          <ul style={{marginTop:6}}>
            <li><b>Hook</b> (média dopamina Part 1–2): <i>ruim</i> {thresholds.hook.bad}, <i>ok</i> {thresholds.hook.ok}, <i>muito bom</i> {thresholds.hook.good}. <Small>Valor atual: {number(k.hookAvg)} (z {number(k.hookZ)}).</Small></li>
            <li><b>Ending Arousal</b> (Part finais; Fear/Surprise/Anticipation/Anger): <i>ruim</i> {thresholds.endArousal.bad}, <i>ok</i> {thresholds.endArousal.ok}, <i>muito bom</i> {thresholds.endArousal.good}. <Small>Atual: {number(k.tailArousal)}.</Small></li>
            <li><b>Ending Positivity</b> (JOY/TRUST finais): <i>ruim</i> {thresholds.endPos.bad}, <i>ok</i> {thresholds.endPos.ok}, <i>muito bom</i> {thresholds.endPos.good}. <Small>Atual: {number(k.tailValPos)}.</Small></li>
            <li><b>EBI</b> = (JOY+TRUST) − (SADNESS+FEAR+DISGUST+ANGER): <i>ruim</i> {thresholds.ebi.bad}, <i>ok</i> {thresholds.ebi.ok}, <i>muito bom</i> {thresholds.ebi.good}. <Small>Atual: {number(k.ebi)}.</Small></li>
            <li><b>Pacing</b> (desvio-padrão do “arousal” por parte): <i>baixo</i> {thresholds.pacing.low}, <i>faixa doce</i> {thresholds.pacing.sweet}, <i>alto</i> {thresholds.pacing.high}. <Small>Atual: {number(k.pacing)}.</Small></li>
          </ul>

          <Small><b>Exemplos</b>: Hook 80 = ótima abertura; Ending Arousal 65 com Ending Pos 30 {`→`} final tenso/negativo; EBI 40 {`→`} sensação geral positiva.</Small>
        </>
      );
    }

    if (infoTab === "Parts") {
      body = (
        <>
          <h3 style={{marginTop:0}}>Como ler a aba “Parts”</h3>
          <Para><b>Lines por emoção</b>: cada linha é uma emoção por parte; clique em um ponto para abrir a cena correspondente.</Para>
          <Para><b>Heatmap</b>: células mais fortes = maior % daquela emoção naquela parte. Clique para ver o trecho.</Para>
          <Para><b>Dopamine (barras)</b>: picos esperados em reviravoltas; linhas guias ajudam a notar “thresholds” (ex. 70).</Para>
          <Para><b>Stacked Area</b>: visão macro do “espaço emocional” ao longo do roteiro.</Para>
          <Small><b>Dicas</b>: Crescente de ANTICIPATION seguido de SURPRISE nas 1–2 partes seguintes é um bom “setup → payoff”. Picos de SADNESS sem recuperação podem “afundar” a energia do meio.</Small>
        </>
      );
    }

  if (infoTab === "Insights") {
    body = (
      <>
        <h3 style={{marginTop:0}}>Como ler a aba “Insights”</h3>
        <Para><b>Emotion Co-occurrence</b>: correlação entre séries das emoções (vermelho {`→`} andam juntas; azul {`→`} opõem-se).</Para>
        <Para><b>Dopamine Correlations</b>: como dopamina relaciona com Arousal/Surprise/etc.</Para>
        <Para><b>Lead–Lag</b>: se ANTICIPATION “paga” em SURPRISE 1–2 partes depois.</Para>
        <Para><b>Transitions</b>: quantas vezes a emoção dominante muda de X para Y.</Para>
        <Para><b>Setups → Payoffs</b> e <b>Improve candidates</b>: sugestões automáticas de ritmo.</Para>
        <Small><b>Exemplo</b>: correlação alta com SURPRISE e arousal, baixa com SADNESS {`→`} bom engajamento sem pesar afetivo.</Small>
      </>
    );
  }

  if (infoTab === "Metadata") {
    body = (
      <>
        <h3 style={{marginTop:0}}>Como ler a aba “Metadata”</h3>
        <Para><b>KPIs</b>: cartões com totais do roteiro — <i>Characters</i> (quantos personagens aparecem falando), <i>Scenes</i> (cenas com cabeçalho), <i>Dialogues</i> (falas capturadas), <i>Locations</i> (lugares únicos).</Para>
        <Para><b>Characters (tabela)</b>: lista de personagens que <u>têm falas</u> nas cenas parseadas. Colunas: Nome, <i>#Lines</i> (quantidade de falas) e <i>Share</i> (% das falas), além do <i>Importance</i> (se disponível). Útil para ver protagonismo e coadjuvantes ativos.</Para>
        <Para><b>Locations (tabela)</b>: locais mais utilizados com <i>#Scenes</i>. Ajuda a entender hubs de cenário (ex.: “PLANTATION”, “HOUSE”).</Para>
        <Para><b>Time of day (pizza)</b>: distribuição de cenas por período (DAY/NIGHT/etc.). Um filme muito diurno tende a ter outra atmosfera que um filme noturno.</Para>
        <Para><b>Character Network (grafo)</b>: nós = personagens (tamanho proporcional a #Lines); arestas = co-ocorrência na <u>mesma cena</u>. Mostra “quem contracena com quem”. Por legibilidade, limitamos aos <i>Top falantes</i>.</Para>
        <Small><b>Exemplos</b>: se 1 personagem concentra &gt;40% das falas → protagonismo forte; se um local concentra &gt;25% das cenas → cenário central; grafo “estrela” (um centro conectando a todos) → narrativa muito centrada nesse personagem.</Small>
      </>
    );
  }

  return (
    <Dialog
      header={`Como interpretar — ${infoTab}`}
      visible={infoOpen}
      onHide={() => setInfoOpen(false)}
      style={{ width: "60vw", maxWidth: 900 }}
      draggable={false}
    >
      {body}
    </Dialog>
  );
  };

  // primeira carga
  useEffect(() => {
    getGeneral().catch(console.error);
  }, [getGeneral]);

  // auto-seleciona o primeiro título quando generalData chega
  useEffect(() => {
    if (!selectedTitle && generalData.length > 0) {
      const first = generalData[0].title;
      const source = generalData[0].source;
      setSelectedTitle(first);
      setSelectedTitleSource(source);
      ensurePartsLoaded(first).catch(console.error);
    }
  }, [generalData, selectedTitle, ensurePartsLoaded]);

  // ativa compare: seleciona um fallback
  useEffect(() => {
    if (compareMode && !compareTitle && titles.length > 1 && selectedTitle) {
      const fallback = titles.find((t) => t.value !== selectedTitle)?.value;
      if (fallback) {
        setCompareTitle(fallback);
        ensurePartsLoaded(fallback).catch(console.error);
      }
    }
  }, [compareMode, compareTitle, titles, selectedTitle, ensurePartsLoaded]);

  const handleTitleChange = useCallback(async (e) => {
    const title = e.value;
    const source = generalData.find((d) => d.title === title)?.source || null;

    setSelectedTitle(title);
    setSelectedTitleSource(source);
    if (title) {
      await ensurePartsLoaded(title);
      setActiveTab("Overview");
    }
  }, [ensurePartsLoaded]);

  const handleCompareToggle = useCallback(async () => {
    const newVal = !compareMode;
    setCompareMode(newVal);
    if (!newVal) {
      setCompareTitle(null);
    } else if (!compareTitle && titles.length > 1) {
      const fallback = titles.find((t) => t.value !== selectedTitle)?.value;
      if (fallback) {
        setCompareTitle(fallback);
        await ensurePartsLoaded(fallback);
      }
    }
  }, [compareMode, compareTitle, titles, selectedTitle, ensurePartsLoaded]);

  const handleCompareTitleChange = useCallback(async (e) => {
    const title = e.value;
    setCompareTitle(title);
    if (title) await ensurePartsLoaded(title);
  }, [ensurePartsLoaded]);

  // ===== modal loader =====
  const openPartModal = useCallback(async (scriptTitle, partNumber) => {
    if (!scriptTitle || !partNumber) return;
    try {
      const res = await axios.get(`${base_url}/get-script-part`, {
        params: { title: scriptTitle, part_number: partNumber },
      });
      setSelectedPartScriptTitle(scriptTitle);
      setSelectedPartNumber(partNumber);
      setSelectedPartText(res.data.part_text);
      setSelectedPartDopa({
        score: res.data.dopamine_score,
        comment: res.data.dopamine_comment,
      });
      setIsModalVisible(true);
    } catch (err) {
      console.error("Error fetching part text", err);
    }
  }, [base_url]);

  // clique genérico
  const handlePlotClick = useCallback((event) => {
    const p = event?.points?.[0];
    if (!p) return;
    const partNumber = Number(p.x);
    const fromTitle = p.data?.meta?.title || selectedTitle;
    if (!Number.isFinite(partNumber)) return;
    openPartModal(fromTitle, partNumber);
  }, [openPartModal, selectedTitle]);

  // upload
  const handleFileSelect = useCallback((e) => setUploadFile(e.files[0]), []);

  const handleUpload = useCallback(async () => {
    if (!uploadTitle || !uploadFile) {
      toast.current.show({ severity: "error", summary: "Error", detail: "Please provide title and file" });
      return;
    }

    setIsProcessing(true);
    const formData = new FormData();
    formData.append("title", uploadTitle);
    formData.append("file", uploadFile);

    loadingIntervalRef.current = setInterval(() => {
      setLoadingMessageIndex((prev) => (prev + 1 < loadingMessages.length ? prev + 1 : prev));
    }, 3000);

    try {
      await axios.post(`${base_url}/upload-sofa-script/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      clearInterval(loadingIntervalRef.current);
      setIsProcessing(false);
      setUploadModalVisible(false);
      setLoadingMessageIndex(0);

      const source = "sofa";

      await getGeneral();
      setSelectedTitle(uploadTitle);
      setSelectedTitleSource(source);
      await ensurePartsLoaded(uploadTitle);

      toast.current.show({ severity: "success", summary: "Success", detail: "Script uploaded successfully" });
      setUploadTitle("");
      setUploadFile(null);
    } catch (err) {
      clearInterval(loadingIntervalRef.current);
      setIsProcessing(false);
      setLoadingMessageIndex(0);
      toast.current.show({ severity: "error", summary: "Error", detail: "Upload failed" });
    }
  }, [uploadTitle, uploadFile, base_url, getGeneral, ensurePartsLoaded]);

  useEffect(() => {
    return () => {
      if (loadingIntervalRef.current) clearInterval(loadingIntervalRef.current);
    };
  }, []);

  // ===== derived (nenhum hook abaixo é condicional) =====
  const getGeneralFor = useCallback(
    (title) => generalData.find((d) => d.title === title),
    [generalData]
  );

  const makeEmotionVector = useCallback(
    (obj) => emotionOrder.map((k) => Number(obj?.[`emotion_pc_${k}`] ?? 0)),
    []
  );

  const partsA = partsMap[selectedTitle] || [];
  const partsB = partsMap[compareTitle] || [];

  const numsA = partsA.map((d) => d.part_number);
  const numsB = partsB.map((d) => d.part_number);
  const dopamineA = partsA.map((d) => d.dopamine_score ?? null);
  const dopamineB = partsB.map((d) => d.dopamine_score ?? null);

  const byEmotionA = useMemo(() => {
    const obj = {};
    emotionOrder.forEach((emo) => { obj[emo] = partsA.map((d) => Number(d[`emotion_pc_${emo}`] ?? 0)); });
    return obj;
  }, [partsA]);

  const byEmotionB = useMemo(() => {
    const obj = {};
    emotionOrder.forEach((emo) => { obj[emo] = partsB.map((d) => Number(d[`emotion_pc_${emo}`] ?? 0)); });
    return obj;
  }, [partsB]);

  const heatZ_A = useMemo(() => emotionOrder.map((emo) => byEmotionA[emo] || []), [byEmotionA]);
  const heatZ_B = useMemo(() => emotionOrder.map((emo) => byEmotionB[emo] || []), [byEmotionB]);

  // ====== INSIGHTS (cálculo) ======
  const calcKPIs = useCallback((title, parts) => {
    const g = getGeneralFor(title) || {};
    const v = makeEmotionVector(g);
    const emo = Object.fromEntries(emotionOrder.map((k, i) => [k, v[i] || 0]));

    // Hook (média dopamina p1..2) + zscore
    const dop = parts.map((p) => Number(p.dopamine_score ?? 0)).filter((x) => x > 0);
    const hookWindow = parts.filter((p) => p.part_number <= 2).map((p) => Number(p.dopamine_score ?? 0)).filter((x) => x > 0);
    const hookAvg = mean(hookWindow);
    const dopMean = mean(dop);
    const dopStd = stdev(dop);
    const hookZ = dopStd ? (hookAvg - dopMean) / dopStd : 0;

    // Ending (últimas 2)
    const maxPart = Math.max(0, ...parts.map(pp => pp.part_number || 0));
    const tail = parts.filter((p) => p.part_number > maxPart - 2);
    const arousalKeys = ["fear","surprise","anticipation","anger"];
    const valPosKeys = ["joy","trust"];
    const tailArousal = mean(tail.map((p) => mean(arousalKeys.map(k => Number(p[`emotion_pc_${k}`] ?? 0)))));
    const tailValPos = mean(tail.map((p) => mean(valPosKeys.map(k => Number(p[`emotion_pc_${k}`] ?? 0)))));

    // EBI
    const ebi = (emo.joy + emo.trust) - (emo.sadness + emo.fear + emo.disgust + emo.anger);

    // Pacing
    const arousalSeries = parts.map((p) => mean(arousalKeys.map(k => Number(p[`emotion_pc_${k}`] ?? 0))));
    const pacing = stdev(arousalSeries);

    return { hookAvg, hookZ, tailArousal, tailValPos, ebi, pacing, emo };
  }, [getGeneralFor, makeEmotionVector]);

  const kpisA = useMemo(() => calcKPIs(selectedTitle, partsA), [selectedTitle, partsA, calcKPIs]);
  const kpisB = useMemo(() => calcKPIs(compareTitle, partsB), [compareTitle, partsB, calcKPIs]);

  const calcCorrelations = useCallback((parts) => {
    const ds = parts.map(p => Number(p.dopamine_score ?? 0));
    const arr = (k) => parts.map(p => Number(p[`emotion_pc_${k}`] ?? 0));
    const corr = (a, b) => {
      if (!a.length || !b.length || a.length !== b.length) return 0;
      const ma = mean(a), mb = mean(b);
      const num = sum(a.map((ai, i) => (ai - ma) * (b[i] - mb)));
      const den = Math.sqrt(sum(a.map(ai => (ai - ma) ** 2)) * sum(b.map(bi => (bi - mb) ** 2)));
      return den ? num / den : 0;
    };
    const arousal = parts.map(p => mean(["fear","surprise","anticipation","anger"].map(k => Number(p[`emotion_pc_${k}`] ?? 0))));
    return {
      withArousal: corr(arousal, ds),
      withSurprise: corr(arr("surprise"), ds),
      withAnticipation: corr(arr("anticipation"), ds),
      withSadness: corr(arr("sadness"), ds),
      withDisgust: corr(arr("disgust"), ds),
    };
  }, []);

  const corrA = useMemo(() => calcCorrelations(partsA), [calcCorrelations, partsA]);
  const corrB = useMemo(() => calcCorrelations(partsB), [calcCorrelations, partsB]);

  const crossCorrelation = useCallback((parts, lead = 1) => {
    const ant = parts.map(p => Number(p[`emotion_pc_anticipation`] ?? 0));
    const sup = parts.map(p => Number(p[`emotion_pc_surprise`] ?? 0));
    if (ant.length <= lead || sup.length <= lead) return 0;
    const a = ant.slice(0, ant.length - lead);
    const b = sup.slice(lead);
    const ma = mean(a), mb = mean(b);
    const num = sum(a.map((ai, i) => (ai - ma) * (b[i] - mb)));
    const den = Math.sqrt(sum(a.map(ai => (ai - ma) ** 2)) * sum(b.map(bi => (bi - mb) ** 2)));
    return den ? num / den : 0;
  }, []);

  const xcorrA = useMemo(() => [crossCorrelation(partsA,1), crossCorrelation(partsA,2)], [partsA, crossCorrelation]);
  const xcorrB = useMemo(() => [crossCorrelation(partsB,1), crossCorrelation(partsB,2)], [partsB, crossCorrelation]);

  const transitionMatrix = useCallback((parts) => {
    const dom = parts.map(p => {
      let best = { k: null, v: -Infinity };
      for (const k of emotionOrder) {
        const v = Number(p[`emotion_pc_${k}`] ?? 0);
        if (v > best.v) best = { k, v };
      }
      return best.k || "joy";
    });
    const idx = Object.fromEntries(emotionOrder.map((k, i) => [k, i]));
    const m = Array.from({ length: emotionOrder.length }, () => Array(emotionOrder.length).fill(0));
    for (let i = 0; i < dom.length - 1; i++) {
      const a = idx[dom[i]], b = idx[dom[i+1]];
      if (a != null && b != null) m[a][b] += 1;
    }
    return m;
  }, []);

  const transA = useMemo(() => transitionMatrix(partsA), [partsA, transitionMatrix]);
  const transB = useMemo(() => transitionMatrix(partsB), [partsB, transitionMatrix]);

  const cooccurrenceCorr = useCallback((parts) => {
    const series = emotionOrder.map(k => parts.map(p => Number(p[`emotion_pc_${k}`] ?? 0)));
    const corr = (a, b) => {
      const ma = mean(a), mb = mean(b);
      const num = sum(a.map((ai, i) => (ai - ma) * (b[i] - mb)));
      const den = Math.sqrt(sum(a.map(ai => (ai - ma) ** 2)) * sum(b.map(bi => (bi - mb) ** 2)));
      return den ? num / den : 0;
    };
    const M = Array.from({ length: 8 }, () => Array(8).fill(0));
    for (let i = 0; i < 8; i++) for (let j = 0; j < 8; j++) M[i][j] = corr(series[i], series[j]);
    return M;
  }, []);

  const coA = useMemo(() => cooccurrenceCorr(partsA), [partsA, cooccurrenceCorr]);
  const coB = useMemo(() => cooccurrenceCorr(partsB), [partsB, cooccurrenceCorr]);

  const improveCandidates = useCallback((parts) => {
    const antKeys = ["fear","surprise","anticipation","anger"];
    const arousal = parts.map(p => mean(antKeys.map(k => Number(p[`emotion_pc_${k}`] ?? 0))));
    const slopes = arousal.map((v, i) => (i ? Math.abs(v - arousal[i-1]) : 0));
    const dopa = parts.map(p => Number(p.dopamine_score ?? 0));
    const sorted = dopa.filter((x)=>x>0).sort((a,b)=>a-b);
    const thrDopa = sorted.length ? sorted[Math.floor(sorted.length*0.3)] : 50; // P30 ou 50
    return parts
      .map((p, i) => ({ part: p.part_number, d: dopa[i], slope: slopes[i] }))
      .filter(r => (r.d || 0) < thrDopa && (r.slope || 0) < 5);
  }, []);

  const gapsA = useMemo(() => improveCandidates(partsA), [partsA, improveCandidates]);
  const gapsB = useMemo(() => improveCandidates(partsB), [partsB, improveCandidates]);

  const setupsPayoffs = useCallback((parts) => {
    const ant = parts.map(p => Number(p[`emotion_pc_anticipation`] ?? 0));
    const sup = parts.map(p => Number(p[`emotion_pc_surprise`] ?? 0));
    const res = [];
    for (let i = 0; i < parts.length - 1; i++) {
      const payoff1 = sup[i+1] || 0;
      const payoff2 = sup[i+2] || 0;
      const score = 0.6 * ant[i] + 0.4 * Math.max(payoff1, payoff2);
      if (ant[i] > 40 && Math.max(payoff1, payoff2) > 40) {
        res.push({
          from: parts[i].part_number,
          to: (payoff2 >= payoff1 ? parts[i+2]?.part_number : parts[i+1]?.part_number) || parts[i+1]?.part_number,
          score: Number(score.toFixed(1))
        });
      }
    }
    return res.sort((a,b) => b.score - a.score).slice(0, 5);
  }, []);

  const spA = useMemo(() => setupsPayoffs(partsA), [partsA, setupsPayoffs]);
  const spB = useMemo(() => setupsPayoffs(partsB), [partsB, setupsPayoffs]);

  // ===== UI subcomponents (não usam hooks) =====
  const WheelOverview = () => {
    // só mostra quando NÃO em comparação
    if (compareMode && compareTitle) return null;

    const genA = getGeneralFor(selectedTitle) || {};
    const r = makeEmotionVector(genA);

    // 8 posições iguais no círculo (0..360)
    const thetaVals = emotionOrder.map((_, i) => (i * 360) / emotionOrder.length);

    // estilos para não “quebrar” horizontalmente
    const wrapStyle = {
      display: "flex",
      flexDirection: "column",
      gap: 12,
      width: "100%",
      maxWidth: 1000,        // limite visual
      margin: "0 auto",      // centraliza
      overflow: "hidden",    // previne scroll lateral
    };

    const dyadsGridStyle = {
      display: "grid",
      gap: 8,
      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", // compacto e responsivo
      alignItems: "stretch",
    };

    const dyadItemStyle = {
      border: "1px solid #eee",
      borderRadius: 8,
      padding: 8,
      background: "#fff",
    };

    return (
      <div style={wrapStyle}>
        <Plot
          data={[
            {
              type: "barpolar",
              r,
              theta: thetaVals,
              marker: {
                color: emotionOrder.map((e) => emotionColors[e]),
                line: { width: 1 },
              },
              hovertext: emotionOrder.map(
                (e, idx) => `${e.toUpperCase()}: ${r[idx]?.toFixed(1) ?? 0}%`
              ),
              hoverinfo: "text",
            },
          ]}
          layout={{
            title: `Plutchik Wheel — ${selectedTitle || ""}`,
            polar: {
              radialaxis: { visible: true, range: [0, 100] },
              angularaxis: {
                direction: "clockwise",
                tickmode: "array",
                tickvals: thetaVals,
                ticktext: emotionOrder.map((e) => e.toUpperCase()),
                tickfont: { size: 11 },
              },
            },
            margin: { t: 50, r: 30, b: 30, l: 30 },
            autosize: true,
            showlegend: false,
          }}
          useResizeHandler
          style={{ width: "100%", height: 420 }}  // altura fixa; 100% da largura do wrapper
        />

        <p style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>
          Ordem Plutchik (sentido horário): JOY → TRUST → FEAR → SURPRISE → SADNESS → DISGUST → ANGER → ANTICIPATION → (wrap) JOY.
        </p>

        <div style={dyadsGridStyle}>
          {plutchikDyads.map((d) => (
            <div key={d.name} style={dyadItemStyle}>
              <strong>{d.name}</strong> — {d.a.toUpperCase()} + {d.b.toUpperCase()}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const RadarCompare = () => {
    if (!compareMode || !compareTitle) return null;
    const genA = getGeneralFor(selectedTitle) || {};
    const genB = getGeneralFor(compareTitle) || {};
    const rA = makeEmotionVector(genA);
    const rB = makeEmotionVector(genB);
    const theta = emotionOrder.map((e) => e.toUpperCase());
    const delta = rA.map((v, i) => v - (rB[i] || 0));

    return (
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
        <Plot
          data={[
            { type: "scatterpolar", r: rA, theta, fill: "toself", name: selectedTitle, line: { shape: "linear" } },
            { type: "scatterpolar", r: rB, theta, fill: "toself", name: compareTitle, line: { shape: "linear", dash: "dash" } },
          ]}
          layout={{
            title: `Radar — ${selectedTitle} vs ${compareTitle}`,
            polar: { radialaxis: { visible: true, range: [0, 100] } },
            margin: { t: 60, r: 40, b: 40, l: 40 },
            autosize: true,
            legend: { orientation: "h", y: -0.2 },
          }}
          useResizeHandler
          style={{ width: "100%", minHeight: "420px" }}
        />
        <Plot
          data={[{ type: "scatterpolar", r: delta.map(d => Math.abs(d)), theta, fill: "toself", name: "|A−B|", line: { shape: "linear" } }]}
          layout={{
            title: "Fingerprint Δ (|A−B|)",
            polar: { radialaxis: { visible: true, range: [0, 100] } },
            margin: { t: 60, r: 40, b: 40, l: 40 },
            autosize: true,
          }}
          useResizeHandler
          style={{ width: "100%", minHeight: "420px" }}
        />
      </div>
    );
  };

  const PieOverview = () => {
    const genA = getGeneralFor(selectedTitle) || {};
    const valuesA = makeEmotionVector(genA);

    if (compareMode && compareTitle) {
      const genB = getGeneralFor(compareTitle) || {};
      const valuesB = makeEmotionVector(genB);
      return (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: "1.5rem" }}>
          <Plot
            data={[{ type: "pie", labels: emotionOrder.map((e) => e.toUpperCase()), values: valuesA, marker: { colors: emotionOrder.map((e) => emotionColors[e]) }, sort: false, textinfo: "label+percent" }]}
            layout={{ title: `Distribution — ${selectedTitle}`, margin: { t: 60, r: 20, b: 20, l: 20 }, autosize: true }}
            useResizeHandler
            style={{ width: "100%", minHeight: "380px" }}
          />
          <Plot
            data={[{ type: "pie", labels: emotionOrder.map((e) => e.toUpperCase()), values: valuesB, marker: { colors: emotionOrder.map((e) => emotionColors[e]) }, sort: false, textinfo: "label+percent" }]}
            layout={{ title: `Distribution — ${compareTitle}`, margin: { t: 60, r: 20, b: 20, l: 20 }, autosize: true }}
            useResizeHandler
            style={{ width: "100%", minHeight: "380px" }}
          />
        </div>
      );
    }

    return (
      <Plot
        data={[{ type: "pie", labels: emotionOrder.map((e) => e.toUpperCase()), values: valuesA, marker: { colors: emotionOrder.map((e) => emotionColors[e]) }, sort: false, textinfo: "label+percent" }]}
        layout={{ title: `General distribution — ${selectedTitle}`, margin: { t: 60, r: 20, b: 20, l: 20 }, autosize: true }}
        useResizeHandler
        style={{ width: "100%", minHeight: "420px" }}
      />
    );
  };

  const Chip = ({ title, value, hint }) => (
    <div title={hint || ""} style={{ padding: "8px 12px", background: "#f6f7f9", border: "1px solid #eaeaea", borderRadius: 10, minWidth: 120 }}>
      <div style={{ fontSize: 12, opacity: 0.7 }}>{title}</div>
      <div style={{ fontWeight: 600 }}>{value}</div>
    </div>
  );

  const KPIsRow = ({ k }) => (
    <div style={{ display: "grid", gridTemplateColumns: compareMode && compareTitle ? "1fr 1fr" : "1fr", gap: "1rem" }}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <Chip title="Hook" value={`${k.hookAvg.toFixed(1)} (z ${k.hookZ.toFixed(2)})`} hint="Média de dopamina nas primeiras partes; z-score vs média do filme." />
        <Chip title="Ending Arousal" value={k.tailArousal.toFixed(1)} hint="Arousal no final (fear/surprise/anticipation/anger)." />
        <Chip title="Ending Positivity" value={k.tailValPos.toFixed(1)} hint="Valência positiva no final (joy/trust)." />
        <Chip title="EBI" value={k.ebi.toFixed(1)} hint="(Joy+Trust) − (Sadness+Fear+Disgust+Anger)." />
        <Chip title="Pacing" value={k.pacing.toFixed(1)} hint="Volatilidade do arousal por parte." />
      </div>
      {compareMode && compareTitle && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Chip title={`Hook — ${compareTitle}`} value={`${kpisB.hookAvg.toFixed(1)} (z ${kpisB.hookZ.toFixed(2)})`} />
          <Chip title="Ending Arousal" value={kpisB.tailArousal.toFixed(1)} />
          <Chip title="Ending Positivity" value={kpisB.tailValPos.toFixed(1)} />
          <Chip title="EBI" value={kpisB.ebi.toFixed(1)} />
          <Chip title="Pacing" value={kpisB.pacing.toFixed(1)} />
        </div>
      )}
    </div>
  );

  // ---- Parts ----
  const PartsLine = ({ scriptTitle, parts, byEmotion }) => {
    const x = parts.map((d) => d.part_number);
    return (
      <Plot
        data={emotionOrder.map((emo) => ({
          x,
          y: byEmotion[emo],
          mode: "lines+markers",
          name: `${emo.toUpperCase()} — ${scriptTitle}`,
          marker: { color: emotionColors[emo] },
          hovertemplate: `Part %{x}<br>${emo.toUpperCase()}: %{y:.1f}%<extra></extra>`,
          meta: { title: scriptTitle },
        }))}
        layout={{
          title: `Emotions per part — ${scriptTitle}`,
          xaxis: { title: "Part" },
          yaxis: { title: "Percentage (%)", range: [0, 100] },
          margin: { t: 60, r: 20, b: 60, l: 50 },
          autosize: true,
          legend: { orientation: "h", y: -0.2 },
        }}
        onClick={handlePlotClick}
        useResizeHandler
        style={{ width: "100%", minHeight: "420px" }}
      />
    );
  };

  const PartsHeatmap = ({ scriptTitle, xNums, z }) => (
    <div>
      <Plot
        data={[
          {
            type: "heatmap",
            z,
            x: xNums,
            y: emotionOrder.map((e) => e.toUpperCase()),
            colorscale: "Viridis",
            colorbar: { title: "%" },
            hoverongaps: false,
            hovertemplate: "Part %{x}<br>%{y}: %{z:.1f}%<extra></extra>",
            meta: { title: scriptTitle },
          },
        ]}
        layout={{
          title: `Emotions heatmap — ${scriptTitle}`,
          xaxis: { title: "Part" },
          yaxis: { title: "Emotion" },
          margin: { t: 60, r: 30, b: 60, l: 80 },
          autosize: true,
        }}
        onClick={handlePlotClick}
        useResizeHandler
        style={{ width: "100%", minHeight: "360px" }}
      />
      <p style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
        Dica: esta “matriz de intensidades” mostra, por linha, cada emoção de Plutchik e, por coluna, as partes do roteiro.
        Células mais fortes = maior % daquela emoção. Clique para abrir o trecho e os picos de dopamina.
      </p>
    </div>
  );

  const DopamineBars = () => {
    // modo comparação: barras agrupadas
    if (compareMode && compareTitle) {
      const x = numsA.length >= numsB.length ? numsA : numsB;
      return (
        <Plot
          data={[
            { type: "bar", x, y: dopamineA, name: selectedTitle, meta: { title: selectedTitle }, hovertemplate: "Part %{x}<br>Dopamine: %{y:.1f}<extra></extra>" },
            { type: "bar", x, y: dopamineB, name: compareTitle,  meta: { title: compareTitle  }, hovertemplate: "Part %{x}<br>Dopamine: %{y:.1f}<extra></extra>" },
          ]}
          layout={{
            title: `Dopamine per part — ${selectedTitle} vs ${compareTitle}`,
            barmode: "group",
            xaxis: { title: "Part" },
            yaxis: { title: "Score (0-100)", range: [0, 100] },
            margin: { t: 60, r: 20, b: 60, l: 50 },
            autosize: true,
          }}
          onClick={handlePlotClick}
          useResizeHandler
          style={{ width: "100%", minHeight: "360px" }}
        />
      );
    }

    // single
    return (
      <Plot
        data={[
          { type: "bar", x: numsA, y: dopamineA, name: "Dopamine score", meta: { title: selectedTitle }, marker: { opacity: 0.95 }, hovertemplate: "Part %{x}<br>Dopamine: %{y:.1f}<extra></extra>" },
          numsA.length > 1 ? { type: "scatter", mode: "lines", x: [numsA[0], numsA[numsA.length - 1]], y: [70, 70], name: "Threshold 70", line: { dash: "dash" }, hoverinfo: "skip" } : null,
        ].filter(Boolean)}
        layout={{
          title: `Dopamine per part — ${selectedTitle}`,
          xaxis: { title: "Part" },
          yaxis: { title: "Score (0-100)", range: [0, 100] },
          margin: { t: 60, r: 20, b: 60, l: 50 },
          autosize: true,
        }}
        onClick={handlePlotClick}
        useResizeHandler
        style={{ width: "100%", minHeight: "360px" }}
      />
    );
  };

  const StackedArea = ({ title, parts }) => {
    const x = parts.map(p => p.part_number);
    const traces = emotionOrder.map((k) => ({
      x,
      y: parts.map(p => Number(p[`emotion_pc_${k}`] ?? 0)),
      type: "scatter",
      mode: "lines",
      stackgroup: "one",
      name: k.toUpperCase(),
      line: { width: 1 },
      fill: "tonexty",
      marker: { color: emotionColors[k] },
      hovertemplate: `Part %{x}<br>${k.toUpperCase()}: %{y:.1f}%<extra></extra>`,
      meta: { title },
    }));
    return (
      <div>
        <Plot
          data={traces}
          layout={{
            title: `Emotional arc (stacked) — ${title}`,
            xaxis: { title: "Part" },
            yaxis: { title: "Percentage (%)", range: [0, 100] },
            margin: { t: 60, r: 20, b: 60, l: 50 },
            autosize: true,
            showlegend: true,
            legend: { orientation: "h", y: -0.2 },
          }}
          useResizeHandler
          style={{ width: "100%", minHeight: "420px" }}
          onClick={handlePlotClick}
        />
        <p style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
          Visão macro do arco emocional: a área mostra o “espaço narrativo” ocupado por cada emoção ao longo das partes.
        </p>
      </div>
    );
  };

  const SimpleHeat = ({ title, z, zTitle }) => (
    <div>
      <Plot
        data={[{ type: "heatmap", z, x: emotionOrder.map(e=>e.toUpperCase()), y: emotionOrder.map(e=>e.toUpperCase()), colorscale: "RdBu", reversescale: true }]}
        layout={{
          title: `${zTitle} — ${title}`,
          xaxis: { title: "Emotion" },
          yaxis: { title: "Emotion" },
          margin: { t: 60, r: 30, b: 60, l: 80 },
          autosize: true,
        }}
        useResizeHandler
        style={{ width: "100%", minHeight: "360px" }}
      />
      <p style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
        Mapa de calor: vermelho = correlação positiva, azul = negativa, branco = neutro. Ajuda a ver sinergias e tensões entre emoções.
      </p>
    </div>
  );

  const ListCards = ({ title, items, render, emptyText }) => (
    <div>
      <h4 style={{ margin: "0 0 8px" }}>{title}</h4>
      {items.length ? (
        <div style={{ display: "grid", gap: 8 }}>
          {items.map((it, i) => (
            <div key={i} style={{ border: "1px solid #eee", borderRadius: 8, padding: 10 }}>
              {render(it, i)}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ opacity: 0.7 }}>{emptyText}</div>
      )}
    </div>
  );

  // filtro da lista de acervos
  const filteredAcervos = useMemo(() => {
    const q = (acervosQuery || "").toLowerCase().trim();
    if (!q) return ACERVOS;
    return ACERVOS.filter(a =>
      a.name.toLowerCase().includes(q) ||
      a.url.toLowerCase().includes(q) ||
      (a.note || "").toLowerCase().includes(q)
    );
  }, [acervosQuery]);

  const isBooting = generalData.length === 0;

  return (
    <div
      style={{
        padding: "2rem",
        maxWidth: "1400px",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
      }}
    >
      <Toast ref={toast} />

      <h2 style={{ textAlign: "center" }}>🎬 IMSDB Script Emotion Dashboard</h2>

      {isBooting ? (
        <div style={{ padding: 20, textAlign: "center" }}>Loading scripts...</div>
      ) : (
        <>
          {/* FILTER BAR */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "1rem",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div style={{ flex: "1 1 280px", maxWidth: "420px" }}>
              <Dropdown
                value={selectedTitle}
                onChange={handleTitleChange}
                options={titles}
                optionLabel="label"
                placeholder="Select a movie script"
                filter
                showClear
                className="w-full"
              />
            </div>

            <Button
              label={compareMode ? "Comparing: ON" : "Compare"}
              icon="pi pi-clone"
              className={compareMode ? "p-button-warning" : "p-button-outlined"}
              onClick={handleCompareToggle}
            />

            <div style={{ flex: "1 1 280px", maxWidth: "420px" }}>
              <Dropdown
                value={compareTitle}
                onChange={handleCompareTitleChange}
                options={titles.filter((t) => t.value !== selectedTitle)}
                optionLabel="label"
                placeholder="Select script to compare"
                disabled={!compareMode}
                filter
                showClear
                className="w-full"
              />
            </div>

            <Button
              label="Upload Script"
              icon="pi pi-upload"
              onClick={() => setUploadModalVisible(true)}
              className="p-button-success"
            />

            {/* NOVO: botão para abrir acervos */}
            <Button
              label="Acervos online"
              icon="pi pi-book"
              onClick={() => setAcervosOpen(true)}
              className="p-button-secondary p-button-outlined"
            />
          </div>

          {/* ACERVOS MODAL */}
          <Dialog
            header="Acervos online de roteiros"
            visible={acervosOpen}
            onHide={() => { setAcervosOpen(false); setAcervosQuery(""); }}
            style={{ width: "50rem", maxWidth: "90vw" }}
          >
            <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
              <span className="p-input-icon-left" style={{ flex: 1 }}>
                <i className="pi pi-search" />
                <input
                  value={acervosQuery}
                  onChange={(e) => setAcervosQuery(e.target.value)}
                  placeholder="Buscar por nome ou URL..."
                  className="p-inputtext p-component w-full"
                  style={{ paddingLeft: 32 }}
                />
              </span>
              <Button label="Limpar" className="p-button-text" onClick={() => setAcervosQuery("")} />
            </div>

            <div style={{ display: "grid", gap: 10 }}>
              {filteredAcervos.map((a) => (
                <div key={a.url} style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, background: "#fff" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                    <div>
                      <div style={{ fontWeight: 700 }}>{a.name}</div>
                      {a.note && <div style={{ fontSize: 12, opacity: 0.8 }}>{a.note}</div>}
                      <div style={{ fontSize: 12, marginTop: 4, opacity: 0.85 }}>
                        <a href={a.url} target="_blank" rel="noopener noreferrer">{a.url}</a>
                      </div>
                    </div>
                    <div>
                      <Button
                        label="Abrir"
                        icon="pi pi-external-link"
                        onClick={() => window.open(a.url, "_blank", "noopener,noreferrer")}
                      />
                    </div>
                  </div>
                </div>
              ))}
              {!filteredAcervos.length && (
                <div style={{ opacity: 0.7 }}>Nenhum acervo encontrado para “{acervosQuery}”.</div>
              )}
            </div>
          </Dialog>

          {/* UPLOAD MODAL */}
          <Dialog
            header="Upload Script"
            visible={uploadModalVisible}
            onHide={() => setUploadModalVisible(false)}
            style={{ width: "50vw" }}
          >
            <div className="field">
              <label htmlFor="title">Title</label>
              <input
                type="text"
                id="title"
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                className="p-inputtext p-component w-full mb-3"
                placeholder="Enter the script title"
              />
            </div>

            <FileUpload
              name="file"
              customUpload
              auto
              uploadHandler={handleFileSelect}
              accept=".txt"
              maxFileSize={2_000_000}
              chooseLabel="Choose Text File"
            />

            {isProcessing ? (
              <>
                <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
                  <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }} />
                </div>
                <p style={{ textAlign: "center", marginTop: 20 }}>
                  {loadingMessages[loadingMessageIndex]}
                </p>
              </>
            ) : (
              <Button label="Process" onClick={handleUpload} disabled={isProcessing} />
            )}
          </Dialog>

          {/* Heading com título do(s) filme(s) */}
          {(selectedTitle || (compareMode && compareTitle)) && (
            <center>
              <div style={headingWrapStyle}>
                <h2
                  style={headingTitleStyle}
                  title={formatHeadingTitle(selectedTitle, compareTitle, compareMode)}
                >
                  {formatHeadingTitle(selectedTitle, compareTitle, compareMode)}
                </h2>
              </div>
            </center>
          )}

          {/* TABS */}
          {selectedTitle && (
            <div style={{ display: "flex", gap: "1rem", justifyContent: "center", alignItems: "center" }}>
              {TABS.map((t) => (
                <Button
                  key={t}
                  label={t}
                  className={activeTab === t ? "p-button p-button-help" : "p-button-outlined"}
                  onClick={() => setActiveTab(t)}
                />
              ))}
              <Button
                icon="pi pi-info-circle"
                className="p-button-rounded p-button-text"
                onClick={() => { setInfoTab(activeTab); setInfoOpen(true); }}
                tooltip="Como interpretar esta aba"
                aria-label="Como interpretar esta aba"
              />
            </div>
          )}

          {/* OVERVIEW */}
          {selectedTitle && activeTab === "Overview" && (
            <>
              <KPIsRow k={kpisA} />
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))", gap: "2rem" }}>
                <WheelOverview />
                <RadarCompare />
                <PieOverview />
              </div>
            </>
          )}

          {/* PARTS */}
          {selectedTitle && activeTab === "Parts" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
              {(!compareMode || !compareTitle) ? (
                <PartsLine scriptTitle={selectedTitle} parts={partsA} byEmotion={byEmotionA} />
              ) : (
                <>
                  <PartsLine scriptTitle={selectedTitle} parts={partsA} byEmotion={byEmotionA} />
                  <PartsLine scriptTitle={compareTitle} parts={partsB} byEmotion={byEmotionB} />
                </>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: compareMode && compareTitle ? "1fr 1fr" : "1fr",
                  gap: "2rem",
                  alignItems: "start",
                }}
              >
                <PartsHeatmap scriptTitle={selectedTitle} xNums={numsA} z={heatZ_A} />
                {compareMode && compareTitle && (
                  <PartsHeatmap scriptTitle={compareTitle} xNums={numsB} z={heatZ_B} />
                )}
              </div>

              <DopamineBars />

              <StackedArea title={selectedTitle} parts={partsA} />
              {compareMode && compareTitle && <StackedArea title={compareTitle} parts={partsB} />}
            </div>
          )}

          {/* INSIGHTS */}
          {selectedTitle && activeTab === "Insights" && (
            <div style={{ display: "grid", gridTemplateColumns: compareMode && compareTitle ? "1fr 1fr" : "1fr", gap: "2rem" }}>
              {/* Coluna A */}
              <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                <SimpleHeat title={selectedTitle} z={coA} zTitle="Emotion co-occurrence (correlation)" />
                <div>
                  <Plot
                    data={[
                      { type: "bar", x: ["Arousal","Surprise","Anticipation","Sadness","Disgust"], y: [corrA.withArousal,corrA.withSurprise,corrA.withAnticipation,corrA.withSadness,corrA.withDisgust], hovertemplate: "%{x}: %{y:.2f}<extra></extra>" }
                    ]}
                    layout={{ title: `Dopamine correlations — ${selectedTitle}`, yaxis: { range: [-1, 1], title: "r" }, margin: { t: 60, r: 30, b: 60, l: 50 }, autosize: true }}
                    useResizeHandler
                    style={{ width: "100%", minHeight: "320px" }}
                  />
                  <p style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
                    Correlação de dopamina com dimensões-chave. Espera-se correlação ↑ com Arousal/Surprise e ↓ com tristeza/repulsa.
                  </p>
                </div>

                <div>
                  <Plot
                    data={[{ type: "bar", x: ["lag 1","lag 2"], y: xcorrA, hovertemplate: "%{x}: %{y:.2f}<extra></extra>" }]}
                    layout={{ title: `Lead–Lag (anticipation ➜ surprise) — ${selectedTitle}`, yaxis: { range: [-1, 1], title: "r" }, margin: { t: 60, r: 30, b: 60, l: 50 }, autosize: true }}
                    useResizeHandler
                    style={{ width: "100%", minHeight: "280px" }}
                  />
                  <p style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
                    Mede se a antecipação em uma parte “paga” em surpresa 1–2 partes depois.
                  </p>
                </div>

                <Plot
                  data={[{ type: "heatmap", z: transA, x: emotionOrder.map(e=>e.toUpperCase()), y: emotionOrder.map(e=>e.toUpperCase()), colorscale: "Blues" }]}
                  layout={{ title: `Dominant emotion transitions — ${selectedTitle}`, xaxis: { title: "Next" }, yaxis: { title: "From" }, margin: { t: 60, r: 30, b: 60, l: 80 }, autosize: true }}
                  useResizeHandler
                  style={{ width: "100%", minHeight: "360px" }}
                />
                <p style={{ marginTop: -8, fontSize: 12, opacity: 0.8 }}>
                  Matrizes de transição: frequência de mudanças de emoção dominante entre partes consecutivas.
                </p>

                <ListCards
                  title="Top setups → payoffs"
                  items={spA}
                  emptyText="Nenhum padrão forte de setup/payoff encontrado."
                  render={(it) => (<div>Part {it.from} → Part {it.to} — score {it.score}</div>)}
                />

                <ListCards
                  title="Improve candidates (baixa dopamina + pouco movimento)"
                  items={gapsA}
                  emptyText="Sem candidatos claros a ajuste."
                  render={(it) => (<div>Part {it.part} — dopamine {Number(it.d||0).toFixed(1)}, slope {Number(it.slope||0).toFixed(1)}</div>)}
                />
              </div>

              {/* Coluna B (comparação) */}
              {compareMode && compareTitle && (
                <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                  <SimpleHeat title={compareTitle} z={coB} zTitle="Emotion co-occurrence (correlation)" />
                  <div>
                    <Plot
                      data={[
                        { type: "bar", x: ["Arousal","Surprise","Anticipation","Sadness","Disgust"], y: [corrB.withArousal,corrB.withSurprise,corrB.withAnticipation,corrB.withSadness,corrB.withDisgust], hovertemplate: "%{x}: %{y:.2f}<extra></extra>" }
                      ]}
                      layout={{ title: `Dopamine correlations — ${compareTitle}`, yaxis: { range: [-1, 1], title: "r" }, margin: { t: 60, r: 30, b: 60, l: 50 }, autosize: true }}
                      useResizeHandler
                      style={{ width: "100%", minHeight: "320px" }}
                    />
                    <p style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
                      Correlação de dopamina com dimensões-chave.
                    </p>
                  </div>

                  <div>
                    <Plot
                      data={[{ type: "bar", x: ["lag 1","lag 2"], y: xcorrB, hovertemplate: "%{x}: %{y:.2f}<extra></extra>" }]}
                      layout={{ title: `Lead–Lag (anticipation ➜ surprise) — ${compareTitle}`, yaxis: { range: [-1, 1], title: "r" }, margin: { t: 60, r: 30, b: 60, l: 50 }, autosize: true }}
                      useResizeHandler
                      style={{ width: "100%", minHeight: "280px" }}
                    />
                    <p style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
                      Mede se a antecipação “paga” 1–2 partes depois.
                    </p>
                  </div>

                  <Plot
                    data={[{ type: "heatmap", z: transB, x: emotionOrder.map(e=>e.toUpperCase()), y: emotionOrder.map(e=>e.toUpperCase()), colorscale: "Blues" }]}
                    layout={{ title: `Dominant emotion transitions — ${compareTitle}`, xaxis: { title: "Next" }, yaxis: { title: "From" }, margin: { t: 60, r: 30, b: 60, l: 80 }, autosize: true }}
                    useResizeHandler
                    style={{ width: "100%", minHeight: "360px" }}
                  />
                  <p style={{ marginTop: -8, fontSize: 12, opacity: 0.8 }}>
                    Matrizes de transição do comparado.
                  </p>

                  <ListCards
                    title="Top setups → payoffs"
                    items={spB}
                    emptyText="Nenhum padrão forte."
                    render={(it) => (<div>Part {it.from} → Part {it.to} — score {it.score}</div>)}
                  />

                  <ListCards
                    title="Improve candidates"
                    items={gapsB}
                    emptyText="Sem candidatos claros."
                    render={(it) => (<div>Part {it.part} — dopamine {Number(it.d||0).toFixed(1)}, slope {Number(it.slope||0).toFixed(1)}</div>)}
                  />
                </div>
              )}
            </div>
          )}

          {/* METADADOS */}
          {selectedTitle && activeTab === "Metadata" && (
            <MetadataTab
              baseUrl={process.env.REACT_APP_URL_IMSDB_BASE_URL}
              selectedTitle={selectedTitle}
              setSelectedTitle={setSelectedTitle}
              titles={titles}
              source={selectedTitleSource}
            />
          )}

          <ScriptEmotionPartModal
            scriptTitle={selectedPartScriptTitle}
            partText={selectedPartText}
            partNumber={selectedPartNumber}
            dopamineInfo={selectedPartDopa}
            visible={isModalVisible}
            onHide={() => setIsModalVisible(false)}
          />
          <InfoDialog />
        </>
      )}
    </div>
  );
};

export default IMSDBScripts;
